import { computed, inject, toValue, type Ref, type ComputedRef, type MaybeRefOrGetter } from 'vue'
import { ToggleGroupKey } from '../../shared/toggleGroupContext'
import type { ToggleValue } from '../types'

export interface UseButtonToggleOptions {
    /** Whether toggle mode is enabled */
    toggle?: boolean
    /** Value for UiToggleGroup mode */
    value?: ToggleValue
    /** Whether the button is disabled */
    disabled?: MaybeRefOrGetter<boolean | undefined>
    /** Model value ref for standalone toggle mode */
    modelValue?: Ref<boolean | undefined>
}

export interface UseButtonToggleReturn {
    /** Whether the button is currently toggled/pressed */
    isToggled: ComputedRef<boolean>
    /** Whether the button is disabled (from props or group) */
    isDisabled: ComputedRef<boolean>
    /** Whether the group uses changeOnHover mode */
    isChangeOnHover: ComputedRef<boolean>
    /** Click handler for toggle behavior */
    handleClick: () => void
    /** Mouse enter handler for changeOnHover mode */
    handleMouseEnter: () => void
}

/**
 * Composable for adding toggle functionality to button components.
 * Supports both standalone toggle mode (with v-model) and UiToggleGroup integration.
 */
export function useButtonToggle(options: UseButtonToggleOptions): UseButtonToggleReturn {
    const group = inject(ToggleGroupKey, null)

    const isToggled = computed(() => {
        if (group && options.value !== undefined) {
            return group.selected() === options.value
        }
        if (options.toggle && options.modelValue) {
            return !!options.modelValue.value
        }
        return false
    })

    const isDisabled = computed(() => {
        return toValue(options.disabled) || group?.disabled || false
    })

    const isChangeOnHover = computed(() => {
        return group?.changeOnHover || false
    })

    function handleClick() {
        if (isDisabled.value) return
        if (group?.changeOnHover) return

        if (group && options.value !== undefined) {
            group.select(options.value)
            return
        }

        if (options.toggle && options.modelValue) {
            options.modelValue.value = !options.modelValue.value
        }
    }

    function handleMouseEnter() {
        if (isDisabled.value) return
        if (group && options.value !== undefined && group.changeOnHover) {
            group.select(options.value)
        }
    }

    return {
        isToggled,
        isDisabled,
        isChangeOnHover,
        handleClick,
        handleMouseEnter,
    }
}
